<template>
  <div class="container">
    <div class="sidebar">
      <analytics-sidebar @select="handleSelect" :model="model" />
    </div>
    <div class="content">
      <el-card>
        <div slot="header" class="header">
          <span class="header-title">
            {{ $t(section_title[selectedMenuItem]) }} {{ workflowVariantTitle }}
          </span>
          <div class="header-control">
            <el-button
              v-if="isAllChannelsTab && $can('exportCampaignAnalytics')"
              icon="el-icon-s-data"
              class="engagement-btn m-top-1"
              type="primary"
              plain
              @click="onSaveAsReportClick('all_channels')"
            >
              {{ $t('CampaignAnalytics_Engagement_Btn_SaveAsReport') }}
            </el-button>
            <div v-if="model.ab && isJourneyActionsTab">
              <enum-select
                class="variant-selector m-top-1"
                :options="variantOptions"
                v-model="journeyAction"
                title
                :placeholder="
                  $t('CampaignAnalytics_Engagement_Btn_SelectVariant')
                "
                :disabled="journeyActionsLoading"
              />
            </div>
            <div v-if="isJourneyActionsTab && $can('exportCampaignAnalytics')">
              <el-dropdown
                @command="onSaveAsReportClick"
                trigger="click"
                style="margin-right: 8px;"
              >
                <el-button
                  class="engagement-btn m-top-1"
                  icon="el-icon-s-data"
                  type="primary"
                  plain
                >
                  {{ $t('CampaignAnalytics_Engagement_Btn_SaveAsReport') }}
                  <i class="el-icon-arrow-down el-icon--right" />
                </el-button>
                <el-dropdown-menu slot="dropdown">
                  <el-dropdown-item command="journey_actions">
                    <icon
                      style="margin-bottom: -2px; margin-right: 5px"
                      glyph="campaign-multistage"
                    />
                    {{
                      $t(
                        'CampaignAnalytics_Engagement_Btn_Export_JourneyActions'
                      )
                    }}
                  </el-dropdown-item>
                  <el-dropdown-item command="currently_waiting">
                    <icon
                      style="margin-bottom: -2px; margin-right: 5px"
                      glyph="stopwatch"
                    />
                    {{
                      $t(
                        'CampaignAnalytics_Engagement_Btn_Export_CurrentlyWaiting'
                      )
                    }}
                  </el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>
              <el-dropdown @command="handleDataExport" trigger="click">
                <el-button
                  class="engagement-btn m-top-1"
                  icon="el-icon-download"
                  type="primary"
                  plain
                >
                  {{ $t('CampaignAnalytics_Engagement_Btn_Export') }}
                  <i class="el-icon-arrow-down el-icon--right" />
                </el-button>
                <el-dropdown-menu slot="dropdown">
                  <el-dropdown-item command="engagement">
                    <icon
                      style="margin-bottom: -2px; margin-right: 5px"
                      glyph="campaign-multistage"
                    />
                    {{
                      $t(
                        'CampaignAnalytics_Engagement_Btn_Export_JourneyActions'
                      )
                    }}
                  </el-dropdown-item>
                  <el-dropdown-item command="waiting">
                    <icon
                      style="margin-bottom: -2px; margin-right: 5px"
                      glyph="stopwatch"
                    />
                    {{
                      $t(
                        'CampaignAnalytics_Engagement_Btn_Export_CurrentlyWaiting'
                      )
                    }}
                  </el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>
            </div>
            <el-button
              v-if="isAllChannelsTab && $can('exportCampaignAnalytics')"
              icon="el-icon-download"
              class="engagement-btn m-top-1"
              type="primary"
              plain
              @click="showExportMembersAttributesDialog = true"
            >
              {{ $t('CampaignAnalytics_Engagement_Btn_Export_Analytics') }}
            </el-button>
            <date-filter
              :disabled="isJourneyActionsTab && journeyActionsLoading"
              @change="handlerDateFilter($event)"
              :session-key="sessionKey"
              :use-user-config="false"
            />
          </div>
        </div>
        <component
          :is="component_map[selectedMenuItem]"
          :analytics-date-range="dateRange"
          :journey-action-variant="journeyAction"
          :channel="channel"
          :model="model"
          :key="selectedMenuItem"
          @loading="setJourneyActionsLoading"
        />
      </el-card>
    </div>
    <members-export-add-attributes-dialog
      v-if="showExportMembersAttributesDialog"
      :computed-attributes="false"
      :consent-preferences="false"
    />
  </div>
</template>

<script>
import AnalyticsSidebar from '@/modules/campaign-manage/components/analytics/AnalyticsSidebar.vue'
import AllChannels from '@/modules/campaign-manage/components/analytics/AllChannels.vue'
import FormSection from '@/common/components/form/FormSection.vue'
import DateFilter from '@/common/components/dashboard/DateFilter.vue'
import moment from 'moment/moment'
import {
  getDateFilterRange,
  projectEndpoint,
  projectRoute
} from '@/core/helpers'
import ClickedLinks from '@/modules/campaign-manage/components/analytics/ClickedLinks.vue'
import ClickMap from '@/modules/campaign-manage/components/analytics/ClickMap.vue'
import Domains from '@/modules/campaign-manage/components/analytics/Domains.vue'
import DeliveryFailures from '@/modules/campaign-manage/components/analytics/DeliveryFailures.vue'
import Interactions from '@/modules/campaign-manage/components/analytics/Interactions.vue'
import ChannelSummary from '@/modules/campaign-manage/components/analytics/ChannelSummary.vue'
import VariantTesting from '@/modules/campaign-manage/components/analytics/VariantTesting.vue'
import MembersExportAddAttributesDialog from '@/modules/audiences/components/MembersExportAddAttributesDialog.vue'
import { EventBus } from '@/core/EventBus'
import RestApi from '@/core/services/RestApi'
import JourneyActions from '@/modules/campaign-manage/components/analytics/JourneyActions.vue'
import EnumSelect from '@/common/components/form/EnumSelect.vue'
import WorkflowVariantsTable from '@/modules/campaign-manage/components/analytics/WorkflowVariantsTable.vue'
import { mapGetters } from 'vuex'
import { analyticsReportType } from '@/modules/campaign-manage/enum/AnalyticsReportType'
import { $hasFeature } from '@/core/utils/VueAcl'

export default {
  name: 'EngagementContent',
  components: {
    MembersExportAddAttributesDialog,
    EnumSelect,
    DateFilter,
    FormSection,
    AnalyticsSidebar
  },

  props: {
    model: {
      type: Object
    }
  },
  data() {
    return {
      showExportMembersAttributesDialog: false,
      selectedMenuItem:
        this.model.workflow && $hasFeature('journeyBuilderCampaignAnalytics')
          ? 'journey_actions'
          : 'all_channels',
      dateRange: {
        date_from: '',
        date_to: ''
      },
      section_title: {
        all_channels: 'CampaignAnalytics_Sidebar_AllChannels',
        email_summary: 'CampaignAnalytics_ChannelSummary_Title',
        sms_summary: 'CampaignAnalytics_ChannelSummary_Title',
        mobile_push_summary: 'CampaignAnalytics_ChannelSummary_Title',
        web_push_summary: 'CampaignAnalytics_ChannelSummary_Title',
        inbox_summary: 'CampaignAnalytics_ChannelSummary_Title',
        inapp_summary: 'CampaignAnalytics_ChannelSummary_Title',
        onsite_summary: 'CampaignAnalytics_ChannelSummary_Title',
        email_variant_testing: 'CampaignAnalytics_VariantTesting_Title',
        sms_variant_testing: 'CampaignAnalytics_VariantTesting_Title',
        mobile_push_variant_testing: 'CampaignAnalytics_VariantTesting_Title',
        web_variant_testing: 'CampaignAnalytics_VariantTesting_Title',
        inbox_variant_testing: 'CampaignAnalytics_VariantTesting_Title',
        inapp_variant_testing: 'CampaignAnalytics_VariantTesting_Title',
        onsite_variant_testing: 'CampaignAnalytics_VariantTesting_Title',
        variants: 'CampaignAnalytics_VariantTesting_Title',
        email_clicked_links: 'CampaignAnalytics_ClickedLinks_Title',
        sms_clicked_links: 'CampaignAnalytics_ClickedLinks_Title',
        email_click_map: 'CampaignAnalytics_ClickMap_Title',
        email_domains: 'CampaignAnalytics_Domains_Title',
        email_delivery_failures: 'CampaignAnalytics_Delivery_Failures_Title',
        sms_delivery_failures: 'CampaignAnalytics_Delivery_Failures_Title',
        mobile_push_delivery_failures:
          'CampaignAnalytics_Delivery_Failures_Title',
        web_push_delivery_failures: 'CampaignAnalytics_Delivery_Failures_Title',
        inapp_delivery_failures: 'CampaignAnalytics_Delivery_Failures_Title',
        inbox_delivery_failures: 'CampaignAnalytics_Delivery_Failures_Title',
        onsite_delivery_failures: 'CampaignAnalytics_Delivery_Failures_Title',
        web_push_interactions: 'CampaignAnalytics_Interactions_Title',
        mobile_push_interactions: 'CampaignAnalytics_Interactions_Title',
        inapp_interactions: 'CampaignAnalytics_Interactions_Title',
        onsite_interactions: 'CampaignAnalytics_Interactions_Title',
        journey_actions: 'CampaignAnalytics_JourneyActions_Title'
      },
      component_map: {
        all_channels: AllChannels,
        email_summary: ChannelSummary,
        sms_summary: ChannelSummary,
        mobile_push_summary: ChannelSummary,
        web_push_summary: ChannelSummary,
        inbox_summary: ChannelSummary,
        inapp_summary: ChannelSummary,
        onsite_summary: ChannelSummary,
        email_variant_testing: VariantTesting,
        sms_variant_testing: VariantTesting,
        mobile_push_variant_testing: VariantTesting,
        web_push_variant_testing: VariantTesting,
        inbox_variant_testing: VariantTesting,
        inapp_variant_testing: VariantTesting,
        onsite_variant_testing: VariantTesting,
        variants: WorkflowVariantsTable,
        email_clicked_links: ClickedLinks,
        sms_clicked_links: ClickedLinks,
        email_click_map: ClickMap,
        email_domains: Domains,
        email_delivery_failures: DeliveryFailures,
        sms_delivery_failures: DeliveryFailures,
        mobile_push_delivery_failures: DeliveryFailures,
        web_push_delivery_failures: DeliveryFailures,
        inapp_delivery_failures: DeliveryFailures,
        inbox_delivery_failures: DeliveryFailures,
        onsite_delivery_failures: DeliveryFailures,
        web_push_interactions: Interactions,
        mobile_push_interactions: Interactions,
        inapp_interactions: Interactions,
        onsite_interactions: Interactions,
        journey_actions: JourneyActions
      },
      channel: '',
      journeyAction: 'A',
      journeyActionsLoading: false
    }
  },

  created() {
    let dateRange = getDateFilterRange(this.sessionKey)
    this.handlerDateFilter(dateRange)
  },

  mounted() {
    EventBus.$on(
      'export-members-add-attributes-hide',
      () => (this.showExportMembersAttributesDialog = false)
    )
    EventBus.$on('export-members-run-show', this.runExport)
  },

  methods: {
    runExport(params) {
      RestApi(
        projectEndpoint({
          method: 'get',
          url: `campaigns/${this.$route.query.id}/analytics/export`,
          params: {
            profile_attributes: params.attributes,
            startDate: this.dateRange.date_from,
            endDate: this.dateRange.date_to
          }
        })
      )
        .then(({ data }) => {
          const route = this.$router.resolve(
            projectRoute({
              name: 'TaskHistoryView',
              query: { id: data.task_id }
            })
          )
          window.open(route.href, '_blank')
        })
        .catch(error => {
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
    },
    handleSelect({ key, keyPath }) {
      this.channel = keyPath[0]
      this.selectedMenuItem = key
    },

    handlerDateFilter(dateRange) {
      this.dateRange.date_from = moment(dateRange.start).format('YYYY-MM-DD')
      this.dateRange.date_to = moment(dateRange.end).format('YYYY-MM-DD')
    },

    handleDataExport(type) {
      if (type === 'engagement') {
        const route = this.$router.resolve({
          name: 'WorkflowEngagementReportExport',
          query: {
            id: this.model.id,
            project_id: this.$route.query.project_id,
            startDate: this.dateRange.date_from,
            endDate: this.dateRange.date_to,
            variantKey: this.journeyAction
          }
        })

        window.open(route.href, '_blank')
      } else if (type === 'waiting') {
        const route = this.$router.resolve({
          name: 'WorkflowCurrentlyWaitingReportExport',
          query: {
            id: this.model.id,
            project_id: this.$route.query.project_id,
            variantKey: this.journeyAction
          }
        })

        window.open(route.href, '_blank')
      }
    },

    onSaveAsReportClick(type) {
      this.$router.push({
        name: 'CampaignReport',
        query: {
          id: this.model.id,
          project_id: this.$route.query.project_id,
          report_type: analyticsReportType[type]
        }
      })
    },

    setJourneyActionsLoading(loading) {
      this.journeyActionsLoading = loading
    }
  },

  computed: {
    ...mapGetters(['userUid']),
    sessionKey() {
      return `config.${this.userUid}-campaign-${this.model.id}.dateRange`
    },
    isAllChannelsTab() {
      return this.selectedMenuItem === 'all_channels'
    },

    isJourneyActionsTab() {
      return this.selectedMenuItem === 'journey_actions'
    },

    workflowVariantTitle() {
      if (this.model.ab && this.isJourneyActionsTab) {
        return (
          ' / ' +
          this.$t('CampaignAnalytics_Variant') +
          ' ' +
          this.journeyAction
        )
      }
    },

    variantOptions() {
      const variants = {}
      this.model.ab_variants.forEach(
        variant =>
          (variants[variant] =
            this.$t('CampaignAnalytics_Variant') + ' ' + variant)
      )

      return variants
    }
  },
  destroyed() {
    EventBus.$off('export-members-add-attributes-hide')
    EventBus.$off('export-members-run-show')
  }
}
</script>

<style scoped lang="scss">
.container {
  display: grid;
  grid-template-columns: 228px auto;
  gap: 32px;
  grid-template-areas: 'sidebar content';

  .sidebar {
    grid-area: sidebar;
  }

  .content {
    grid-area: content;
    overflow: hidden;

    .header {
      display: flex;
      justify-content: space-between;
      align-items: start;

      .header-title {
        align-self: center;
        font-size: 18px;
        font-weight: 600;
        line-height: 28px;
      }

      .header-control {
        display: flex;
        column-gap: 10px;

        .engagement-btn {
          height: 40px;
          margin-top: 1px;
        }

        .variant-selector {
          >>> input {
            border-color: #1d87c8;
          }

          >>> i {
            color: #1d87c8;
            font-weight: bolder;
          }
        }
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/modules/campaign-manage/components/analytics/EngagementContent.vue